import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Target, Zap, Users, Trophy } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 sm:pt-24 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          {/* Hero Section */}
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent">
              About MCQ AI
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Revolutionizing the way students learn and prepare for exams with AI-powered quiz generation
            </p>
          </div>

          {/* Mission */}
          <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 sm:p-8 mb-8 shadow-card">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Our Mission</h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              At MCQ AI, we believe that learning should be accessible, engaging, and tailored to each individual's needs. 
              Our mission is to empower students and professionals worldwide with intelligent, adaptive quiz generation 
              that helps them master any subject efficiently.
            </p>
          </Card>

          {/* Features */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 shadow-card">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-3 rounded-xl">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Personalized Learning</h3>
                  <p className="text-sm text-muted-foreground">
                    Generate quizzes tailored to your specific topics and difficulty preferences
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 shadow-card">
              <div className="flex items-start space-x-4">
                <div className="bg-success/10 p-3 rounded-xl">
                  <Zap className="h-6 w-6 text-success" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Instant Generation</h3>
                  <p className="text-sm text-muted-foreground">
                    AI-powered quiz creation in seconds with high-quality questions
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 shadow-card">
              <div className="flex items-start space-x-4">
                <div className="bg-warning/10 p-3 rounded-xl">
                  <Users className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Share & Collaborate</h3>
                  <p className="text-sm text-muted-foreground">
                    Share quizzes with friends, classmates, or study groups instantly
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 shadow-card">
              <div className="flex items-start space-x-4">
                <div className="bg-destructive/10 p-3 rounded-xl">
                  <Trophy className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Track Progress</h3>
                  <p className="text-sm text-muted-foreground">
                    Monitor your learning journey with detailed analytics and history
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Story */}
          <Card className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 sm:p-8 shadow-card">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Our Story</h2>
            <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
              <p>
                MCQ AI was born from a simple observation: students spend countless hours creating practice tests 
                and study materials, time that could be better spent actually learning.
              </p>
              <p>
                Leveraging the latest advances in artificial intelligence, we've created a platform that generates 
                high-quality multiple-choice questions instantly. Whether you're preparing for exams, conducting 
                training sessions, or just curious to test your knowledge, MCQ AI makes it effortless.
              </p>
              <p>
                Today, we're proud to serve thousands of learners worldwide, helping them achieve their educational 
                goals faster and more effectively than ever before.
              </p>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
